import { err, ok, Result } from '../../../tracking-api';
import { TinCanLaunchParameters } from './tincan-launch-parameters-resolver';
import XAPI, { Statement, Agent, Result as XApiResult, Verb, Context, Activity } from '@xapi/xapi';
import { injectable } from 'inversify';

export type AgentError = 'unable-to-parse';
export type InitializeError = 'invalid-agent' | 'missing-activity-id';
export type QueryErrorCode = 'query-error';

interface QueryError {
  code: QueryErrorCode;
  details: unknown;
}

interface State { [key: string]: any; }

function queryError(details: any) {
  return err({ code: 'query-error' as QueryErrorCode, details });
}

export type UpdateErrorCode = 'update-error';
interface UpdateError {
  code: UpdateErrorCode;
  details: unknown;
}

function updateError(details: any) {
  return err({ code: 'update-error' as UpdateErrorCode, details });
}

export interface StatementPayload {
  verb: Verb;
  object: Activity;
  result?: XApiResult;
  context?: Context;
}

@injectable()
export class XApiClient {

  private api: XAPI;
  private agent: Agent;
  private activityId: string;
  private registration?: string;

  public initialize(params: TinCanLaunchParameters): Result<true, InitializeError> {

    if (!params.activityId) {
      return err('missing-activity-id');
    }

    this.activityId = params.activityId;
    this.registration = params.registration || undefined;

    const agentResult = this.parseAgent(params.actor);
    if (!agentResult.success) {
      return err('invalid-agent');
    }

    this.agent = agentResult.value;

    this.api = new XAPI(params.endpoint, params.auth || '');
    return ok(true);

    // const myStatement: Statement = { actor: {
    //   objectType: 'Agent'
    // }, verb: {
    //   id: 'alma',
    //   display: {
    //   }
    // },
    //                                  object: {
    //                                    id: 'korte',
    //                                    objectType: 'Activity'
    //                                  }};
    // xApi.sendStatement(myStatement)

    // try {
    //   this.lrs = new TinCan.LRS(
    //     {
    //       endpoint: params.endpoint,
    //       auth: params.auth,
    //       allowFail: false
    //     })
    // } catch (err) {
    //   console.log("Failed to setup LRS object: ", err);
    // }
  }

  public async finish() {
    console.log('closing client');
  }

  public async sendStatement(payload: StatementPayload) {

    const id = payload.object.id ? this.activityId + '/' + payload.object.id : this.activityId;
    const object = {
      ...payload.object,
      id
    };

    const statement: Statement = {
      ...payload,
      actor: this.agent,
      object,
      context: {
        ...(payload.context || {}),
        registration: this.registration
      }
    };

    console.log('Sending statement', statement);

    await this.api.sendStatement(statement);
  }

  public async loadState(stateId: string): Promise<Result<State | null, QueryError>> {

    const hasState = await this.hasState(stateId);
    if (!hasState.success) {
      return err(hasState.error);
    }

    if (!hasState.value) {
      return ok(null);
    }

    try {
      const result = await this.api.getState(this.agent, this.activityId, stateId, this.registration);
      return ok(result);
    } catch (err) {
      return queryError(err);
    }
  }

  public async saveState(stateId: string, state: State): Promise<Result<true, UpdateError>> {
    try {
      await this.api.setState(this.agent, this.activityId, stateId, state, this.registration);
      return ok(true);
    } catch (err) {
      return updateError(err);
    }
  }

  private parseAgent(actor: string): Result<Agent, AgentError> {
    let parsed: any;
    try {
      parsed = JSON.parse(actor);
    } catch (err) {
      return err('unable-to-parse');
    }

    return ok(parsed);
  }

  private async hasState(state: string): Promise<Result<boolean, QueryError>> {
    try {
      const states = await this.api.getStates(this.agent, this.activityId, this.registration);
      return ok(states.indexOf(state) >= 0);
    } catch (err) {
      return queryError(err);
    }
  }

  get rootActivityId() {
    return this.activityId;
  }
}

